/**
 * @file Solutions.tsx
 * @description Solutions page describing Ultimind's Agentic AI products and scenarios.
 */

/**
 * SolutionsPage component details core Ultimind solution offerings.
 */
export default function SolutionsPage() {
  const solutions = [
    {
      title: 'Enterprise Agent Platform',
      body:
        'A visual orchestration platform that turns business requirements into multi-agent workflows. Connects to internal systems, APIs, and low-code tools to automate complex processes end-to-end.',
      bullets: [
        'Role-based agents for planning, research, coding, and execution.',
        'Support for loops, conditional logic, and long-running tasks.',
        'Integrated monitoring, logging, and human-in-the-loop controls.',
      ],
    },
    {
      title: 'Verdant.AI – Wealth Intelligence',
      body:
        'An intelligent wealth analysis and report generation system that automates Source of Wealth (SoW) and KYC workflows for financial institutions.',
      bullets: [
        'Ingests payslips, tax returns, investment portfolios, and property records.',
        'Performs validation, calculations, and benchmark comparisons.',
        'Generates structured, regulator-ready wealth reports with full traceability.',
      ],
    },
    {
      title: 'Smart Tourism & Destination Agents',
      body:
        'Multi-agent solutions for resorts, cultural attractions, and tourism operators that coordinate marketing, booking, and on-site services.',
      bullets: [
        'Scenario-based marketing with real-time visitor insights.',
        'Member apps that connect guests, on-site staff, and backend systems.',
        'Unified dashboards for operations, events, and merchant collaboration.',
      ],
    },
    {
      title: 'Intelligent Operations for Manufacturing & Government',
      body:
        'Agents embedded into zero-code platforms to automate routine tasks from workshop management to digital governance.',
      bullets: [
        'Automated form processing, ticket routing, and status tracking.',
        'Cross-department workflows that break information silos.',
        'Data-driven recommendations based on operational telemetry.',
      ],
    },
  ]

  return (
    <div className="bg-slate-950">
      <section className="mx-auto max-w-5xl px-4 pb-16 pt-10 md:px-6 md:pt-14">
        <h1 className="text-3xl font-semibold tracking-tight text-slate-50 md:text-4xl">
          Solutions &amp; Products
        </h1>
        <p className="mt-4 max-w-2xl text-sm text-slate-300 md:text-base">
          Ultimind delivers Agentic AI solutions that are deeply embedded into
          enterprise workflows. Our systems combine multi-agent coordination
          with retrieval augmented generation and secure tool-use.
        </p>

        <div className="mt-10 space-y-8">
          {solutions.map((solution) => (
            <section
              key={solution.title}
              className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5"
            >
              <h2 className="text-lg font-semibold text-sky-300 md:text-xl">
                {solution.title}
              </h2>
              <p className="mt-3 text-xs text-slate-300 md:text-sm">
                {solution.body}
              </p>
              <ul className="mt-3 space-y-2 text-xs text-slate-300 md:text-sm">
                {solution.bullets.map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="mt-[6px] h-1.5 w-1.5 flex-none rounded-full bg-sky-400" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </section>
    </div>
  )
}
